import type { HTMLAttributes, ReactNode } from "react";

const EASE = [0.22, 1, 0.36, 1] as const;

type BaseProps = {
  children: ReactNode;
  /** Vertical offset in px (default 18). */
  y?: number;
  /** Duration in seconds (default 0.6). */
  duration?: number;
  /** Viewport amount threshold (default 0.15). */
  amount?: number;
  /** Delay in seconds (default 0). */
  delay?: number;
};

type RevealProps = BaseProps & Omit<HTMLAttributes<HTMLDivElement>, keyof BaseProps>;

/**
 * Reveal — shared scroll-triggered fade-up for a single block.
 *
 * Editorial motion language used across the site:
 * 18px y-offset, 0.6s cubic-bezier(0.22, 1, 0.36, 1), fires once at
 * 15% in view, respects prefers-reduced-motion automatically.
 *
 * For a staggered grid, wrap each item in <RevealItem> and pass an
 * index, or compose with framer's own variants in the parent.
 */
const Reveal = ({ children, y: _y, duration: _duration, amount: _amount, delay: _delay, ...rest }: RevealProps) => (
  <div {...rest}>
    {children}
  </div>
);

type RevealItemProps = BaseProps & {
  /** Item index in the list, used to derive the stagger delay. */
  index?: number;
  /** Per-item stagger in seconds (default 0.07). */
  stagger?: number;
} & Omit<HTMLAttributes<HTMLDivElement>, keyof BaseProps | "index" | "stagger">;

/**
 * RevealItem — single grid/list item that fades up in sequence based
 * on its index. Use inside any grid where each card should animate in.
 *
 *   {items.map((item, i) => (
 *     <RevealItem key={item.id} index={i}>
 *       <Card {...item} />
 *     </RevealItem>
 *   ))}
 */
const RevealItem = ({
  children,
  index: _index,
  stagger: _stagger,
  y: _y,
  duration: _duration,
  amount: _amount,
  delay: _delay,
  ...rest
}: RevealItemProps) => (
  <div {...rest}>
    {children}
  </div>
);

export default Reveal;
export { RevealItem, EASE as REVEAL_EASE };
